import { useState } from "react";
import { Search, Pencil, X, Check, Loader2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { Pagination } from "@/components/pagination";

interface NaceCode {
  id: number;
  code: string;
  name: string;
}

interface NaceResponse {
  data: NaceCode[];
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

interface NaceModalData {
  id?: number;
  code: string;
  name: string;
}

export function DataNacePage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<NaceModalData>({ code: "", name: "" });
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "30");
  if (search) queryParams.set("q", search);

  const { data, loading, error, refetch } = useFetch<NaceResponse>(
    `/data/nace?${queryParams.toString()}`,
    [page, search]
  );

  async function handleSave(id: number) {
    setSaving(true);
    try {
      await adminFetch(`/data/nace/${id}`, {
        method: "PUT",
        body: JSON.stringify({ name: editName }),
      });
      setEditingId(null);
      refetch();
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item: NaceCode) {
    setEditingId(item.id);
    setEditName(item.name);
  }

  function openCreateModal() {
    setModalData({ code: "", name: "" });
    setModalError("");
    setModalOpen(true);
  }

  function openEditModal(item: NaceCode) {
    setModalData({ id: item.id, code: item.code, name: item.name });
    setModalError("");
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setModalData({ code: "", name: "" });
    setModalError("");
  }

  async function handleModalSave() {
    if (!modalData.code.trim() || !modalData.name.trim()) {
      setModalError("Kod ve açıklama alanları zorunludur.");
      return;
    }

    setModalSaving(true);
    setModalError("");
    try {
      if (modalData.id) {
        // Edit existing
        await adminFetch(`/data/nace/${modalData.id}`, {
          method: "PUT",
          body: JSON.stringify({ name: modalData.name.trim() }),
        });
      } else {
        // TODO: POST endpoint for creating new NACE codes does not exist yet.
        // When the API is ready, uncomment the following:
        // await adminFetch("/data/nace", {
        //   method: "POST",
        //   body: JSON.stringify({ code: modalData.code.trim(), name: modalData.name.trim() }),
        // });
        setModalError("Yeni NACE kodu ekleme API'si henüz hazır değil.");
        setModalSaving(false);
        return;
      }
      closeModal();
      refetch();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Bir hata oluştu");
    } finally {
      setModalSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="NACE kodu veya açıklama ara..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-border bg-secondary pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:w-80"
          />
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          Yeni NACE Ekle
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : !data || data.data.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            {search
              ? "Arama sonucu bulunamadı."
              : "Henüz NACE kodu bulunmuyor."}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-32 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Kod
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Açıklama
                    </th>
                    <th className="w-20 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      İşlem
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((item) => (
                    <tr
                      key={item.id}
                      className="transition-colors hover:bg-secondary/50"
                    >
                      <td className="px-4 py-3">
                        <code className="rounded bg-secondary px-1.5 py-0.5 text-sm font-mono text-accent">
                          {item.code}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        {editingId === item.id ? (
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-8 w-full rounded-md border border-accent bg-secondary px-2 text-sm text-foreground focus:outline-none"
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm text-foreground">
                            {item.name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === item.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleSave(item.id)}
                              disabled={saving}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-success hover:bg-success/10"
                              aria-label="Kaydet"
                            >
                              {saving ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Check className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
                              aria-label="İptal"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openEditModal(item)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                            aria-label="Düzenle"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.pagination.page}
              totalPages={data.pagination.totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/60"
            onClick={closeModal}
          />
          <div className="relative z-10 w-full max-w-lg rounded-xl border border-border/60 bg-card p-6 shadow-xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                {modalData.id ? (
                  <>
                    NACE Düzenle -{" "}
                    <code className="text-accent">{modalData.code}</code>
                  </>
                ) : (
                  "Yeni NACE Ekle"
                )}
              </h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {modalError && (
                <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {modalError}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Kod
                </label>
                <input
                  type="text"
                  value={modalData.code}
                  onChange={(e) =>
                    setModalData({ ...modalData, code: e.target.value })
                  }
                  disabled={!!modalData.id}
                  placeholder="Örn: 01.11"
                  className={cn(
                    "h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent",
                    modalData.id && "cursor-not-allowed opacity-60"
                  )}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Açıklama
                </label>
                <textarea
                  value={modalData.name}
                  onChange={(e) =>
                    setModalData({ ...modalData, name: e.target.value })
                  }
                  rows={3}
                  placeholder="NACE kodu açıklaması"
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  onClick={handleModalSave}
                  disabled={modalSaving}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    modalSaving && "cursor-not-allowed opacity-50"
                  )}
                >
                  {modalSaving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
